import { clients } from 'lib/https';
import { ConfigurationSettings } from 'models/configuration-settings';

// Types for invitation API
export type InvitationType = 'ADD_USER' | 'REMOVE_USER' | 'ADD_ASSISTANT';

export interface InvitationPayload {
    invitationType: InvitationType;
    assistantId?: string;
    agentId?: string;
    conversationId?: string;
    permissionType?: 'user' | 'assistant';
    isTemporary?: boolean;
    isDashboard?: boolean;
    customGptId?: string;
    /** Agent config preview conversations. */
    type?: 'test';
}

export interface InvitationResponse {
    success: boolean;
    message?: string;
    data?: any;
    conversationId?: string;
}

export interface GroupChatShareTokenPayload {
    conversationId: string;
    permissionType: 'user' | 'assistant';
    expiry?: number;
}

export interface GroupChatShareTokenResponse {
    token: string;
}

export interface GroupChatTokenVerifyResponse {
    tenantId: string;
    conversationId: string;
    conversationTitle: string;
    createdBy: string;
    createdDate: string; // ISO date
    isArchived: boolean;
    lastUpdatedDate: string; // ISO date

    participants: Record<string, unknown>;
    messages: unknown[];
    events: unknown[];

    projectId: string;
    isTemporary: boolean;
    shareMetadata: unknown[];
}


/**
 * Sends an invitation to users for a group chat conversation.
 *
 * @param {InvitationPayload} payload - The invitation payload containing type, user IDs, and conversation ID.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<InvitationResponse>} The response from the invitation API.
 *
 * @throws {Error} If the request fails or the server returns an error.
 *
 * @example
 * const response = await sendInvitation({
 *   invitationType: 'ADD_USER',
 *   userIds: ['user1', 'user2'],
 *   conversationId: 'conv123'
 * }, config);
 */
export const sendInvitation = async (
    payload: InvitationPayload,
    config: ConfigurationSettings
): Promise<InvitationResponse> => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.invitation}`;

    return await clients.post<InvitationResponse>(
        url,
        config.blocksKey,
        JSON.stringify(payload)
    );
};


export const getGroupChatParticipants = async (conversationId: string, config: ConfigurationSettings) => {

    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.invitation}?conversationId=${conversationId}`;
    return await clients.get<any>(url, config.blocksKey);

}

export const getGroupChatShareToken = async (conversationId: string, config: ConfigurationSettings) => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.invitation}/token`;
    const payload: GroupChatShareTokenPayload = {
        conversationId,
        permissionType: "user"
    }
    return await clients.post<GroupChatShareTokenResponse>(url, config.blocksKey, JSON.stringify(payload));
}

export const joinGroup = async (token: string, config: ConfigurationSettings) => {
    const baseUrl = `${config.paths.seliseAiBaseUrl}`;
    const url = `${baseUrl}${config.paths.seliseAi.invitation}/${token}`;
    const payload = {};
    return await clients.post<GroupChatTokenVerifyResponse>(url, config.blocksKey, JSON.stringify(payload));
};